import { defineStore } from 'pinia';
import {
  computed,
  ref,
  useRuntimeConfig,
  navigateTo,
  nextTick,
  useCookie,
  type Ref,
} from '#imports';
import {
  createAuthSessionController,
  asAuthUser,
  runLogoutFlow,
  usePermissionChecker,
  hasRole,
  hasMatch,
  getAuthCookieOptions,
} from 'r3ui-lib';
import { fetchAuthUser, logOutSSO } from '~/services/auth.service';
import {
  isUnauthorizedError,
  presentPageLoadError,
} from '~/utils/presentPageLoadError';

export const useAuthStore = defineStore(
  'auth',
  () => {
    const config = useRuntimeConfig();
    const { authApiUrl, origin } = config.public;
    // Опции обязаны совпадать с теми, с которыми cookie ставит серверный
    // callback: без domain удаление не снимет cookie родительского домена.
    const cookie = useCookie<string | null>(
      'auth_token',
      getAuthCookieOptions({
        origin: origin as string | undefined,
        authCookieDomain: config.public.authCookieDomain as string | undefined,
      })
    ) as Ref<string | null>;
    const token = ref<string | null | undefined>(cookie.value);
    const user = ref<Record<string, unknown> | null>(null);

    const isAuthenticated = computed(() => {
      return Boolean(token.value);
    });

    const hasUserPermission = usePermissionChecker(
      computed(() => user.value?.all_permissions ?? [])
    );

    const userRoleNames = computed(() => {
      if (!Array.isArray(user.value?.roles)) {
        return [];
      }

      return user.value.roles
        .map((role: { name?: string }) => role?.name)
        .filter((roleName: string | undefined): roleName is string =>
          Boolean(roleName)
        );
    });

    const hasUserRole = (role: string | string[]) => {
      if (Array.isArray(role)) {
        return hasMatch(role, userRoleNames.value);
      }

      return hasRole(userRoleNames.value, role);
    };

    const session = createAuthSessionController({
      token,
      user,
      async loadUser(requestedToken) {
        const loaded = await fetchAuthUser(requestedToken);
        const userRecord = asAuthUser<Record<string, unknown>>(loaded);
        if (!userRecord) throw new Error('fetchUser: пустой ответ');
        return userRecord;
      },
      isUnauthorized: isUnauthorizedError,
      onError: presentPageLoadError,
      clearCookie: () => {
        cookie.value = null;
      },
      runLogout,
    });
    const { fetchUser, logout } = session;

    function resetStorage() {
      const storageLinks = ['breadcrumbs', 'filters', 'tables'];
      storageLinks.map(i => {
        localStorage.removeItem(i);
      });
    }

    async function runLogout(resetSession: () => void) {
      const baseLogoutUrl = authApiUrl ? `${authApiUrl}/logout` : '/login';
      const redirectPath = origin
        ? `${baseLogoutUrl}?redirect=${encodeURIComponent(origin)}`
        : baseLogoutUrl;
      const navigateWithExternal = (url: string): void | Promise<void> => {
        if (/^https?:\/\//.test(url)) {
          return navigateTo(url, { external: true }) as void | Promise<void>;
        }
        return navigateTo(url) as void | Promise<void>;
      };
      await runLogoutFlow({
        logOutSSO,
        nextTick,
        navigateTo: navigateWithExternal,
        onReset: () => {
          resetSession();
          resetStorage();
        },
        onError: (error: unknown) => {
          // eslint-disable-next-line no-console
          console.error('[logout]', error);
        },
        redirectPath,
      });
    }

    return {
      isAuthenticated,
      logout,
      fetchUser,
      token,
      user,
      hasUserPermission,
      hasUserRole,
    };
  },
  {
    persist: {
      pick: ['user'],
    },
  }
);
